import React, { ReactElement, useState } from 'react';
import { BlockInput } from 'components/worksheet/common/BlockInput';
import { Box, Text, VStack, NumberInput, HStack } from '@chakra-ui/react';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { DeleteIcon } from 'assets/icons/v2';
import { getBlockValue, updateBlockWithContentMigration } from 'utils/blockMigrationUtils';
import { SABlockProps } from '../SelfAssessmentBlock';

type Props = SABlockProps & {
  onDelete?: () => void;
};

export const TeacherSABlock = ({ block, editor, onDelete }: Props): ReactElement => {
  const [question, setQuestion] = useState(getBlockValue(block, 'question'));
  const [minScore, setMinScore] = useState(block.props.minScore || 0);
  const [maxScore, setMaxScore] = useState(block.props.maxScore || 5);

  const handleQuestionChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setQuestion(e.target.value);
    updateBlockWithContentMigration(editor, block, 'question', e.target.value);
  };

  const handleMinScoreChange = (valueAsString: string, valueAsNumber: number): void => {
    if (Number.isNaN(valueAsNumber)) {
      setMinScore(0);
      block.props.minScore = 0;
    } else {
      setMinScore(valueAsNumber);
      block.props.minScore = valueAsNumber;
    }
  };

  const handleMaxScoreChange = (valueAsString: string, valueAsNumber: number): void => {
    if (Number.isNaN(valueAsNumber)) {
      setMaxScore(minScore);
      block.props.maxScore = minScore;
    } else {
      setMaxScore(valueAsNumber);
      block.props.maxScore = valueAsNumber;
    }
  };

  const jumpToEditor = (): void => {
    // Focus the parent editor
    editor.focus();

    // Move cursor to the end of the current block
    editor.setTextCursorPosition(block, 'end');

    // Create a new block by simulating Enter key press
    const enterEvent = new KeyboardEvent('keydown', {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      which: 13,
      bubbles: true,
      cancelable: true,
      composed: true,
    });

    // Dispatch the event on the editor's root element
    editor.domElement?.dispatchEvent(enterEvent);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      jumpToEditor();
    }
  };

  return (
    <VStack
      border="default"
      borderColor="border.accent"
      borderRadius="l"
      p="l"
      gap="l"
      width="100%"
      alignItems="flex-start"
      bg="grey.white"
      contentEditable={false}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
        <Text textStyle="body.m.medium" color="text.primary">
          Selbsteinschätzung
        </Text>
        {onDelete && (
          <NovaIconButton
            aria-label="Selbsteinschätzung löschen"
            variant="primary"
            size="m"
            onClick={onDelete}
            _hover={{
              '& svg': {
                color: 'red.500',
              },
            }}
            boxSize="18px"
            fontSize="18px"
            lineHeight="0"
            minWidth="0px"
            icon={DeleteIcon}
            iconProps={{ fill: 'transparent', color: 'icon.quaternary' }}
          />
        )}
      </Box>

      <BlockInput
        label="Frage"
        name="question"
        value={question}
        onChange={handleQuestionChange}
        placeholder="Gib deine Frage hier ein..."
        showLineBreakHint
        onKeyDown={handleKeyDown}
      />

      <HStack display="flex" gap="10px">
        <NumberInput.Root
          max={maxScore}
          value={minScore.toString()}
          onValueChange={(details) => handleMinScoreChange(details.value, details.valueAsNumber)}
          w="50px"
          textAlign="center"
          clampValueOnBlur
        >
          <NumberInput.Input p={0} textAlign="center" />
        </NumberInput.Root>
        <Text textStyle="body.s.medium" color="text.primary">
          &nbsp;-&nbsp;
        </Text>
        <NumberInput.Root
          min={minScore + 1}
          value={maxScore.toString()}
          onValueChange={(details) => handleMaxScoreChange(details.value, details.valueAsNumber)}
          w="50px"
          textAlign="center"
          clampValueOnBlur
        >
          <NumberInput.Input p={0} textAlign="center" />
        </NumberInput.Root>
      </HStack>
    </VStack>
  );
};
